import React, { useState } from "react";
import { ScanSearch, CheckCircle2, XCircle, Info } from "lucide-react";
import { motion } from "framer-motion";
import { useI18n } from "@/i18n/I18nProvider";
import { useSEO } from "@/hooks/useSEO";
import { PageHero } from "@/components/shared/PageHero";
import { PageCta } from "@/components/shared/PageCta";
import { Btn, Section } from "@/components/ui-kit/Kit";
import { Reveal } from "@/components/motion/Reveal";
import { verifyCode } from "@/lib/api";

export default function Verify() {
  const { t } = useI18n();
  const p = t("pages.verify");
  const [code, setCode] = useState("");
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  useSEO({ title: p.title, description: p.lead, path: "/verify" });

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setResult(null);
    setLoading(true);
    try {
      const d = await verifyCode(code);
      setResult(d);
    } catch (err) {
      const d = err?.response?.data?.detail;
      setError(typeof d === "string" ? d : "Verification failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <PageHero eyebrow={p.eyebrow} title={p.title} lead={p.lead} />
      <Section className="py-20 sm:py-24" data-testid="verify-page">
        <div className="grid gap-14 lg:grid-cols-[1.1fr_1fr] lg:gap-20">
          <div>
            <form onSubmit={onSubmit} className="border border-navy/10 bg-white p-8 sm:p-10" data-testid="verify-form">
              <label htmlFor="gpu-code" className="font-mono text-[10px] uppercase tracking-[0.22em] text-navy/50">
                GPU Member / Vendor Code
              </label>
              <div className="mt-4 flex flex-col gap-3 sm:flex-row">
                <div className="relative flex-1">
                  <ScanSearch className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-navy/35" />
                  <input
                    id="gpu-code"
                    value={code}
                    onChange={(e) => setCode(e.target.value.toUpperCase())}
                    placeholder={p.placeholder}
                    data-testid="verify-input"
                    className="w-full border border-navy/15 py-3.5 pl-11 pr-4 font-mono text-sm tracking-[0.1em] text-navy outline-none transition-colors duration-200 focus:border-grn"
                  />
                </div>
                <Btn
                  variant="navy"
                  radius="rounded-none"
                  type="submit"
                  disabled={loading || !code}
                  data-testid="verify-submit"
                  className="disabled:opacity-40"
                >
                  {loading ? t("common.loading") : p.button}
                </Btn>
              </div>
              <p className="mt-5 text-[12px] leading-relaxed text-slate-500">{p.note}</p>
            </form>

            {error && (
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-6 flex items-start gap-3 border border-red-200 bg-red-50 p-5"
                data-testid="verify-error"
              >
                <XCircle className="mt-0.5 h-4 w-4 shrink-0 text-red-600" />
                <p className="text-[13px] text-red-800">{error}</p>
              </motion.div>
            )}

            {result && !result.found && (
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-6 flex items-start gap-3 border border-navy/15 bg-slate-50 p-5"
                data-testid="verify-not-found"
              >
                <Info className="mt-0.5 h-4 w-4 shrink-0 text-navy/50" />
                <p className="text-[13px] text-slate-600">{p.notFound}</p>
              </motion.div>
            )}

            {result?.found && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-6 border border-grn/30 bg-grn-pale p-8"
                data-testid="verify-result"
              >
                <div className="flex items-center gap-3">
                  <CheckCircle2 className="h-5 w-5 text-grn" />
                  <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-grn">
                    {result.record.code}
                  </span>
                </div>
                <dl className="mt-7 grid gap-5 sm:grid-cols-2">
                  {["firm", "district", "status", "validity", "type"].map((k) =>
                    result.record[k] ? (
                      <div key={k}>
                        <dt className="font-mono text-[10px] uppercase tracking-[0.2em] text-navy/45">
                          {p.fields[k] || k}
                        </dt>
                        <dd className="mt-1.5 font-display text-base font-medium text-navy">{result.record[k]}</dd>
                      </div>
                    ) : null
                  )}
                </dl>
              </motion.div>
            )}
          </div>

          <Reveal delay={0.1}>
            <div className="border border-navy/10 bg-slate-50/70 p-8 sm:p-10">
              <h2 className="font-display text-xl font-semibold text-navy">Example format</h2>
              <div className="mt-6 space-y-3 font-mono text-[13px] text-navy/70">
                <p>GPU-M-000125 — Member code</p>
                <p>GPU-V-000348 — Vendor code</p>
              </div>
              <p className="mt-8 border-l-2 border-gold pl-5 text-[12px] leading-relaxed text-slate-600">
                {p.disclaimer}
              </p>
            </div>
          </Reveal>
        </div>
      </Section>
      <PageCta />
    </>
  );
}
